import { z } from 'zod'
import { imageAssetRefSchema } from '../helpers/asset-ref.js'
import type { StoredJson } from '../helpers/json.js'
import type { VendorService } from '../enums/vendor-service.enum.js'
import { vendorOfferingKindSchema } from '../enums/vendor-offering-kind.enum.js'
import { vendorPriceUnitSchema } from '../enums/vendor-price-unit.enum.js'
import { vendorPriceVisibilitySchema } from '../enums/vendor-price-visibility.enum.js'
import {
  MAX_OFFERING_PHOTOS,
  MAX_VENDOR_OFFERINGS,
  MAX_VENDOR_PHOTO_CAPTION,
  OFFERING_DESCRIPTION_MAX,
  OFFERING_LIST_ITEMS_MAX,
  OFFERING_LIST_ITEM_MAX,
  OFFERING_NAME_MAX,
  PRICE_CENTS_MAX,
} from '../constants/vendor-profile.js'

/**
 * What a vendor offers: packages and items, each for one of its services, with
 * photos, a price (or "price on request") and the fields of that service's
 * kind (docs/vendor-growth.spec.md, decided 2026-10-23).
 *
 * Furniture is not here: a furniture company keeps its catalog, priced per
 * piece and placed on plans.
 */

/** A service that can have packages: every one but furniture. */
export type OfferedService = Exclude<VendorService, 'furniture'>

const listOf = () =>
  z
    .array(z.string().trim().min(1).max(OFFERING_LIST_ITEM_MAX))
    .max(OFFERING_LIST_ITEMS_MAX, `Up to ${String(OFFERING_LIST_ITEMS_MAX)}`)
    .default([])

const hours = z.number().min(0.5).max(24).multipleOf(0.5).nullable().default(null)
const minutes = z.number().int().min(0).max(24 * 60).nullable().default(null)

export const DIETARY_TAGS = ['vegetarian', 'vegan', 'gluten_free', 'dairy_free', 'nut_free', 'halal', 'kosher'] as const
export type DietaryTag = (typeof DIETARY_TAGS)[number]
export const DIETARY_TAG_LABELS: Record<DietaryTag, string> = {
  vegetarian: 'Vegetarian',
  vegan: 'Vegan',
  gluten_free: 'Gluten-free',
  dairy_free: 'Dairy-free',
  nut_free: 'Nut-free',
  halal: 'Halal',
  kosher: 'Kosher',
}

export const FLORAL_PIECES = ['centerpiece', 'bouquet', 'ceremony', 'other'] as const
export type FloralPiece = (typeof FLORAL_PIECES)[number]
export const FLORAL_PIECE_LABELS: Record<FloralPiece, string> = {
  centerpiece: 'Centerpiece',
  bouquet: 'Bouquet',
  ceremony: 'Ceremony',
  other: 'Other',
}

export const PLANNING_TIERS = ['full', 'partial', 'day_of'] as const
export type PlanningTier = (typeof PLANNING_TIERS)[number]
export const PLANNING_TIER_LABELS: Record<PlanningTier, string> = {
  full: 'Full planning',
  partial: 'Partial planning',
  day_of: 'Day of',
}

/**
 * Each service's fields, built fresh on every call.
 *
 * Called twice. The write copy is `.strict()` and the response copy is not, and
 * they must not be the same object: a strict details schema inside a response
 * DTO rejects a field added later and the whole profile fails to parse. The
 * guard in `response-shapes.test.ts` only sees `.strict()` written in a dto
 * file, so this import used to slip past it.
 */
function offeringDetailObjects() {
  const equipment = () => z.object({ equipment: listOf(), setupMinutes: minutes, teardownMinutes: minutes })
  return {
    catering: z.object({ courses: listOf(), dietaryTags: z.array(z.enum(DIETARY_TAGS)).max(DIETARY_TAGS.length).default([]) }),
    photography: z.object({ hours, deliverables: listOf() }),
    entertainment: z.object({ setLengthHours: hours, equipment: listOf() }),
    floral: z.object({ piece: z.enum(FLORAL_PIECES).nullable().default(null) }),
    beauty: z.object({ services: listOf(), trialIncluded: z.boolean().default(false), onSite: z.boolean().default(false) }),
    transportation: z.object({
      vehicleTypes: listOf(),
      seats: z.number().int().min(1).max(500).nullable().default(null),
      minimumHours: hours,
    }),
    lighting: equipment(),
    av: equipment(),
    planning: z.object({
      tier: z.enum(PLANNING_TIERS).nullable().default(null),
      monthsOfInvolvement: z.number().int().min(1).max(36).nullable().default(null),
    }),
  }
}

/** What a profile reads back. Unknown keys are dropped; the package still loads. */
export const offeringDetailsReadSchemas = offeringDetailObjects()

/** What a write is held to, and what stored JSON is settled against. Every one defaults from `{}`. */
const writtenDetails = offeringDetailObjects()
export const offeringDetailsSchemas = {
  catering: writtenDetails.catering.strict(),
  photography: writtenDetails.photography.strict(),
  entertainment: writtenDetails.entertainment.strict(),
  floral: writtenDetails.floral.strict(),
  beauty: writtenDetails.beauty.strict(),
  transportation: writtenDetails.transportation.strict(),
  lighting: writtenDetails.lighting.strict(),
  av: writtenDetails.av.strict(),
  planning: writtenDetails.planning.strict(),
} satisfies Record<OfferedService, z.ZodType>

export type CateringDetails = z.infer<typeof offeringDetailsSchemas.catering>
export type PhotographyDetails = z.infer<typeof offeringDetailsSchemas.photography>
export type EntertainmentDetails = z.infer<typeof offeringDetailsSchemas.entertainment>
export type FloralDetails = z.infer<typeof offeringDetailsSchemas.floral>
export type BeautyDetails = z.infer<typeof offeringDetailsSchemas.beauty>
export type TransportationDetails = z.infer<typeof offeringDetailsSchemas.transportation>
export type EquipmentDetails = z.infer<typeof offeringDetailsSchemas.lighting>
export type PlanningDetails = z.infer<typeof offeringDetailsSchemas.planning>

/** A photo on a package or item, as written. */
export const offeringPhotoInputSchema = z
  .object({
    image: imageAssetRefSchema,
    caption: z.string().trim().max(MAX_VENDOR_PHOTO_CAPTION).nullable().default(null),
  })
  .strict()

const offeringBase = {
  kind: vendorOfferingKindSchema,
  name: z.string().trim().min(1, 'Name it').max(OFFERING_NAME_MAX),
  description: z.string().trim().max(OFFERING_DESCRIPTION_MAX).default(''),
  priceVisibility: vendorPriceVisibilitySchema,
  /** Cents. Null exactly when the price is on request. */
  priceCents: z.number().int().min(0).max(PRICE_CENTS_MAX, 'That price is too high').nullable(),
  /** "Starting at" rather than a set price. */
  priceFrom: z.boolean().default(false),
  priceUnit: vendorPriceUnitSchema,
  photos: z.array(offeringPhotoInputSchema).max(MAX_OFFERING_PHOTOS, `Up to ${String(MAX_OFFERING_PHOTOS)} photos`).default([]),
}

function member<S extends OfferedService, D extends z.ZodType>(service: S, details: D) {
  return z.object({ service: z.literal(service), details, ...offeringBase }).strict()
}

/**
 * The money rules, which cross fields: a shown price has an amount, a price on
 * request has none (it is never stored, so it can never leak), and only
 * flowers are priced per table.
 */
function priceRules(
  v: { service: OfferedService; priceVisibility: 'shown' | 'on_request'; priceCents: number | null; priceFrom: boolean; priceUnit: string },
  ctx: z.RefinementCtx,
): void {
  if (v.priceVisibility === 'shown' && v.priceCents === null) {
    ctx.addIssue({ code: 'custom', path: ['priceCents'], message: 'Enter a price, or choose Price on request' })
  }
  if (v.priceVisibility === 'on_request' && v.priceCents !== null) {
    ctx.addIssue({ code: 'custom', path: ['priceCents'], message: 'A price on request has no price' })
  }
  if (v.priceVisibility === 'on_request' && v.priceFrom) {
    ctx.addIssue({ code: 'custom', path: ['priceFrom'], message: 'A price on request has no starting price' })
  }
  if (v.priceUnit === 'table' && v.service !== 'floral') {
    ctx.addIssue({ code: 'custom', path: ['priceUnit'], message: 'Only flowers are priced per table' })
  }
}

/**
 * One package or item, as written: created whole, and replaced whole when it
 * is edited, its photos included.
 */
export const vendorOfferingInputSchema = z
  .discriminatedUnion('service', [
    member('catering', offeringDetailsSchemas.catering),
    member('photography', offeringDetailsSchemas.photography),
    member('entertainment', offeringDetailsSchemas.entertainment),
    member('floral', offeringDetailsSchemas.floral),
    member('beauty', offeringDetailsSchemas.beauty),
    member('transportation', offeringDetailsSchemas.transportation),
    member('lighting', offeringDetailsSchemas.lighting),
    member('av', offeringDetailsSchemas.av),
    member('planning', offeringDetailsSchemas.planning),
  ])
  .superRefine(priceRules)

export type VendorOfferingInput = z.infer<typeof vendorOfferingInputSchema>

/** The packages' whole order, every time (as the gallery's). */
export const reorderVendorOfferingsInputSchema = z
  .object({
    offeringIds: z
      .array(z.string().uuid())
      .min(1, 'Send the packages in the order they should appear.')
      .max(MAX_VENDOR_OFFERINGS)
      .refine((ids) => new Set(ids).size === ids.length, 'Each package can only appear once.'),
  })
  .strict()

export type ReorderVendorOfferingsInput = z.infer<typeof reorderVendorOfferingsInputSchema>

/** Whether a service can have packages (furniture keeps its catalog). */
export function isOfferedService(service: VendorService): service is OfferedService {
  return service !== 'furniture'
}

/** A service paired with its own fields, so the two can't disagree. */
export type OfferingServiceDetails =
  | { service: 'catering'; details: CateringDetails }
  | { service: 'photography'; details: PhotographyDetails }
  | { service: 'entertainment'; details: EntertainmentDetails }
  | { service: 'floral'; details: FloralDetails }
  | { service: 'beauty'; details: BeautyDetails }
  | { service: 'transportation'; details: TransportationDetails }
  | { service: 'lighting'; details: EquipmentDetails }
  | { service: 'av'; details: EquipmentDetails }
  | { service: 'planning'; details: PlanningDetails }

/**
 * A stored offering's fields, read back with its service. Stored JSON that no
 * longer parses (a field renamed since it was written) gives that service's
 * defaults rather than failing the whole profile, and says it substituted so
 * the caller can log it.
 */
export function offeringServiceDetails(service: OfferedService, stored: StoredJson): { value: OfferingServiceDetails; substituted: boolean } {
  switch (service) {
    case 'catering': {
      const read = settle(offeringDetailsSchemas.catering, stored)
      return { value: { service, details: read.details }, substituted: read.substituted }
    }
    case 'photography': {
      const read = settle(offeringDetailsSchemas.photography, stored)
      return { value: { service, details: read.details }, substituted: read.substituted }
    }
    case 'entertainment': {
      const read = settle(offeringDetailsSchemas.entertainment, stored)
      return { value: { service, details: read.details }, substituted: read.substituted }
    }
    case 'floral': {
      const read = settle(offeringDetailsSchemas.floral, stored)
      return { value: { service, details: read.details }, substituted: read.substituted }
    }
    case 'beauty': {
      const read = settle(offeringDetailsSchemas.beauty, stored)
      return { value: { service, details: read.details }, substituted: read.substituted }
    }
    case 'transportation': {
      const read = settle(offeringDetailsSchemas.transportation, stored)
      return { value: { service, details: read.details }, substituted: read.substituted }
    }
    case 'lighting': {
      const read = settle(offeringDetailsSchemas.lighting, stored)
      return { value: { service, details: read.details }, substituted: read.substituted }
    }
    case 'av': {
      const read = settle(offeringDetailsSchemas.av, stored)
      return { value: { service, details: read.details }, substituted: read.substituted }
    }
    case 'planning': {
      const read = settle(offeringDetailsSchemas.planning, stored)
      return { value: { service, details: read.details }, substituted: read.substituted }
    }
    default: {
      const unexpected: never = service
      return unexpected
    }
  }
}

function settle<T>(schema: z.ZodType<T>, stored: StoredJson): { details: T; substituted: boolean } {
  const parsed = schema.safeParse(stored)
  return parsed.success ? { details: parsed.data, substituted: false } : { details: schema.parse({}), substituted: true }
}
